﻿module SafeStack.Client.Tooltip

open Feliz
open Feliz.Tippy
open Fable.React
open Fable.React.Props

let private createInternal customPlacement isInteractive content anchorToElement =
    Tippy.create [
        yield Tippy.plugins [|
            Plugins.followCursor
            Plugins.animateFill
            Plugins.inlinePositioning
        |]
        yield Tippy.content (div [] content)
        yield prop.children [
            anchorToElement
        ]
        yield Tippy.placement (customPlacement |> Option.defaultValue Auto)
        yield Tippy.arrow true
        yield Tippy.hideOnClick Hide
        yield Tippy.zIndex 9999
        yield Tippy.maxWidth 600
        yield Tippy.trigger [ MouseEnter ]
        if isInteractive then yield Tippy.interactive
        //Other supported functions:
        //Tippy.interactive 
        //Tippy.arrow "<svg width=\"100\" height=\"100\"><circle cx=\"50\" cy=\"50\" r=\"40\" stroke=\"green\" stroke-width=\"4\" fill=\"yellow\" /></svg>"
        //Tippy.delay (Milliseconds 500)
        //Tippy.animation (Animation.Scale Extreme)
        //Tippy.inertia
        //Tippy.duration (show = Milliseconds 1000, hide = (Milliseconds 1000))
        //Tippy.animateFill
        //Tippy.followCursor true
        //Tippy.disabled
        //Tippy.getReferenceClientRect
        //    (fun () ->
        //        { Width = 10
        //          Height = 10
        //          Left = 0
        //          Right = 0
        //          Top = 0
        //          Bottom = 0 })
        //Tippy.hideOnClick DontHide
        //Tippy.inlinePositioning
        //Tippy.interactiveBorder 100
        //Tippy.offset { Skidding = 200; Distance = 200 }
        //Tippy.touch (LongPress (TimeSpan.FromSeconds 500.))
    ]


/// <summary>
/// Ensure that the tooltip is anchored to a ReactElement representing an actual HTML tag
/// for example:
/// Fable.React.str will throw an exception; 
/// Fable.React.p will work as expected
/// </summary>
///<exception cref="System.Exception">anchorToElement does not represents an actual HTML tag</exception>
let create content anchorToElement = createInternal None false content anchorToElement


/// <summary>
/// Ensure that the tooltip is anchored to a ReactElement representing an actual HTML tag
/// for example:
/// Fable.React.str will throw an exception; 
/// Fable.React.p will work as expected
/// </summary>
///<exception cref="System.Exception">anchorToElement does not represents an actual HTML tag</exception>
let createInteractive content anchorToElement = createInternal None true content anchorToElement

/// <summary>
/// Ensure that the tooltip is anchored to a ReactElement representing an actual HTML tag
/// for example:
/// Fable.React.str will throw an exception; 
/// Fable.React.p will work as expected
/// </summary>
///<exception cref="System.Exception">anchorToElement does not represents an actual HTML tag</exception>
let createWithPlacement placement interactive content anchorToElement = createInternal (Some placement) interactive content anchorToElement

module UI = 
    open Illy.SafeStack.Client

    let labelWithMandatoryAndTooltip tooltip id lbl validation validationWarning loading mandatory =
        div [ ClassName "label" ] [
            yield label [ 
                ClassName (jQueryUI.addSkeletonScreen ("control-label" + if mandatory then " mandatory" else "") None loading)
                HtmlFor id 
            ] [ 
                match tooltip with 
                | Some t -> 
                    abbr [] [ str lbl ]
                    |> create t
                | None -> str lbl 
            ]

            match validation with 
            | Some s -> yield span [ ClassName "validation ui-state-error" ] [ str s ]
            | None -> ()
            if validation.IsNone then 
                match validationWarning with 
                | Some s -> 
                yield span [ ClassName "validation ui-state-highlight" ] [ str s ]
                | None -> ()
        ]

    let dateWithOptionalTooltip tooltip (model: DatePicker.Model) dispatch loading extras =
        jQueryUI.inputGroup [
            labelWithMandatoryAndTooltip tooltip model.Id model.Label model.ValidationError model.ValidationWarning loading model.IsMandatory        
            DatePicker.dateInput model dispatch loading extras
        ]

    let dateWithTooltip tooltip model dispatch loading extras = dateWithOptionalTooltip (Some tooltip) model dispatch loading extras 

    let selectWithOptionalTooltip tooltip loading (model: 'a Select.Model) dispatch =    
        jQueryUI.inputGroup [
            labelWithMandatoryAndTooltip tooltip model.Id model.Label model.ValidationError model.ValidationWarning loading model.Mandatory
            Select.selectInput loading model dispatch  
        ]

    let selectWithTooltip tooltip loading model dispatch = selectWithOptionalTooltip (Some tooltip) loading model dispatch

    let numberWithOptionalTooltip tooltip loading (model: NumberPicker.Model) dispatch = 
        let control = model.Control
        
        jQueryUI.inputGroup [
            labelWithMandatoryAndTooltip tooltip control.Id control.Label control.Error control.Warning loading model.CustomSettings.IsMandatory       
            NumberPicker.numberInput loading model dispatch
        ]

    let numberWithTooltip tooltip loading model dispatch = numberWithOptionalTooltip (Some tooltip) loading model dispatch

    let multiSelectWithOptionalTooltip tooltip loading (model: 'a MultiSelect.Model) dispatch =    
        jQueryUI.inputGroup [
            labelWithMandatoryAndTooltip tooltip model.Id model.Label model.ValidationError model.ValidationWarning loading model.IsMandatory
            MultiSelect.multiSelectInput loading model dispatch  
        ]
    let multiSelectWithTooltip tooltip loading model dispatch = multiSelectWithOptionalTooltip (Some tooltip) loading model dispatch

    let private renderCheckInline tooltip loading (model: Check.Model) dispatch = 
        let control = model.Control
        jQueryUI.inputGroupWithClasses ("checkbox" :: model.CustomSettings.InputGroupCSSClasses) [
            Check.checkInput loading model dispatch
            labelWithMandatoryAndTooltip tooltip control.Id control.Label control.Error control.Warning loading model.Control.Mandatory       
        ]

    let private renderCheckMultiLine tooltip loading (model: Check.Model) dispatch =
        let control = model.Control
        jQueryUI.inputGroupWithClasses model.CustomSettings.InputGroupCSSClasses [
            labelWithMandatoryAndTooltip tooltip control.Id control.Label control.Error control.Warning loading model.Control.Mandatory      
            Check.checkInput loading model dispatch
        ]

    let checkWithOptionalTooltip tooltip loading (model: Check.Model) dispatch =
        if model.CustomSettings.RenderInline then renderCheckInline tooltip loading model dispatch
        else renderCheckMultiLine tooltip loading model dispatch

    let checkWithTooltip tooltip loading model dispatch = checkWithOptionalTooltip (Some tooltip) loading model dispatch

    let textWithOptionalTooltip tooltip loading (model: Text.Model) dispatch = 
        let control = model.Control
        
        jQueryUI.inputGroup [
            labelWithMandatoryAndTooltip tooltip control.Id control.Label control.Error control.Warning loading model.Control.Mandatory      
            Text.textInput loading model dispatch
        ]
    
    let textWithTooltip tooltip loading model dispatch = textWithOptionalTooltip (Some tooltip) loading model dispatch